<template>
  <v-card class="login-card overflow-hidden">
    <v-img
      v-if="!isMobile"
      class="p-absolute"
      :src="require('@/assets/images/capable_capybara.png')"
      height="100%"
    ></v-img>
    <div class="d-flex flex-column">
      <v-toolbar
        :color="showError ? 'error' : isMobile ? 'primary' : 'rgba(0, 0, 0, 0.20)'"
        dark
        flat
      >
        <v-toolbar-title>{{ toolbarTitle }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <div v-if="!isMobile" class="d-flex flex-column">
          <span class="text-subtitle-2 text-right">v. {{ appVersion }}</span>
          <span class="text-subtitle-2 text-right">{{ appVersionName }}</span>
        </div>
      </v-toolbar>

      <div class="p-absolute mt-16" style="height: 100%; width: 100%; background-color: white; opacity: .97;"></div>

      <div class="z-2 pa-3 overflow-y-auto d-flex flex-column" style="max-height: 370px; height: 370px; min-height: 370px;">
        <template v-if="authSessions.length > 0 && !showLoginForm">
          <div class="d-flex">
            <div class="text-h6">Inloggade användare</div>

            <!-- När fyra eller fler sessions så visa ett sökfält -->
            <v-text-field
              v-if="authSessions.length > 3"
              v-model="searchString"
              append-icon="mdi-magnify"
              label="Sök användare"
              single-line
              hide-details
              class="ml-auto pa-0"
              small
              style="max-width: 200px;"
            ></v-text-field>
          </div>

          <div class="d-flex flex-column">
            <!-- Extra padding för att autoscrollen ska ge lite extra marginal när elementet scrollas into view, men negativ margin för att inte ha för stort avstånd mellan korten -->
            <div
              v-for="(authSession, cardIndex) in filteredAuthSessions"
              :key="authSession.id"
              class="py-4 my--2"
              :class="{ ['session-card-index-' + cardIndex]: true }"
            >
              <v-card
                @click="reconnectSession(authSession.id)"
                data-testid="login-session-card"
                :data-signed-in="String(!!authSession.token)"
                min-width="120"
                class="py-2 px-4 d-flex cursor-pointer"
                :elevation="selectedCardIndex === cardIndex ? 12 : 2"
              >
                <div class="d-flex justify-center align-center" style="width: 50px;">
                  <v-badge
                    :value="!!authSession.token"
                    bordered
                    color="success"
                    dot
                    overlap
                  >
                    <v-avatar :color="authSession.user.color" :size="authSession.user.is_support ? 34 : 50">
                      <span class="white--text" v-text="authSession.user.initials" :style="{ fontSize: authSession.user.is_support ? '0.75em' : '1em' }"></span>
                    </v-avatar>
                  </v-badge>
                </div>
                <div class="ml-4 my-auto d-flex d-flex flex-column">
                  <template v-if="authSession.user.is_support">
                    <!-- Lite smalare kort för support-konton - så vi kan se fler i listan -->
                    <div class="font-weight-bold" style="font-size: 1em;">
                      {{ authSession.domain.name }} ({{ authSession.user.namespace }})
                    </div>
                  </template>
                  <template v-else>
                    <div class="font-weight-light" style="font-size: 1.5em;">
                      {{ authSession.user.username }}
                    </div>
                    <div class="text-subtitle-2">
                      {{ authSession.domain.name }}
                    </div>
                  </template>
                </div>
                <div class="ml-auto my-auto">
                  <v-menu offset-x>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        v-bind="attrs"
                        v-on="on"
                        class="ml-auto my-auto"
                      >
                        <v-icon>
                          mdi-dots-vertical
                        </v-icon>
                      </v-btn>
                    </template>
                    <v-card>
                      <v-list>
                        <v-list-item v-if="!!authSession.token">
                          <v-btn
                            block
                            color="error"
                            text
                            @click="logOutSession(authSession.id)"
                          >
                            Logga ut
                          </v-btn>
                        </v-list-item>
                        <v-list-item v-if="!!authSession.token">
                          <v-btn
                            block
                            color="error"
                            text
                            @click="logOutAndDeleteSession(authSession.id)"
                          >
                            Logga ut och glöm
                          </v-btn>
                        </v-list-item>
                        <v-list-item v-if="!authSession.token">
                          <v-btn
                            block
                            color="error"
                            text
                            @click="logOutAndDeleteSession(authSession.id)"
                          >
                            Ta bort
                          </v-btn>
                        </v-list-item>
                      </v-list>
                    </v-card>
                  </v-menu>
                </div>
              </v-card>
            </div>

            <!-- Lägga till ny användare -->
            <div class="py-4 my--2 login-card-add-new-account">
              <v-card
                @click="loginWithAnotherAccount"
                class="py-2 px-4 d-flex cursor-pointer"
                min-width="120"
                :elevation="selectedCardIndex === filteredAuthSessions.length ? 12 : 2"
              >
                <div class="d-flex justify-center align-center" style="width: 50px;">
                  <v-icon size="30" color="primary">mdi-account-plus</v-icon>
                </div>
                <div class="ml-4 my-auto d-flex d-flex flex-column">
                  <div class="text-body-2">
                    Logga in med ett annat konto
                  </div>
                </div>
              </v-card>
            </div>
          </div>
        </template>

        <template v-else>
          <v-form @submit.prevent="login" class="d-flex flex-column full-height">
            <div class="flex-grow-1">
              <v-autocomplete
                v-model="domainId"
                :items="allDomainsOrdered"
                item-text="name"
                item-value="id"
                prepend-icon="mdi-map-marker-radius"
                label="Välj din anläggning"
              ></v-autocomplete>

              <v-text-field
                v-model="username"
                label="Användarnamn"
                name="username"
                prepend-icon="mdi-account"
                type="text"
              ></v-text-field>

              <v-text-field
                id="password"
                v-model="password"
                label="Lösenord"
                name="password"
                prepend-icon="mdi-lock"
                type="password"
              ></v-text-field>

              <v-alert
                v-if="validationError"
                type="error"
                dense
                class="mt-2"
              >
                {{ validationError }}
              </v-alert>
            </div>

            <div class="d-flex full-width mt-auto">
              <v-btn
                v-if="authSessions.length > 0"
                @click="backToSessionList"
                color="primary"
                class="ma-2"
                text
              >
                Tillbaka
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn
                type="submit"
                color="success"
                class="ma-2"
                :loading="isLoading"
              >
                Logga in
              </v-btn>
            </div>
          </v-form>
        </template>
      </div>
    </div>
  </v-card>
</template>

<script>
import { mapState, mapGetters } from 'vuex'

export default {
  name: 'LoginCard',
  props: {
    isMobile: Boolean
  },
  data: () => ({
    showLoginForm: false,
    username: '',
    password: '',
    domainId: null,
    validationError: '',
    isLoading: false,
    searchString: '',
    selectedCardIndex: 0
  }),
  computed: {
    ...mapState(
      {
        authSessions: state => state.user.authSessions,
        hasTimeoutError: state => state.user.hasTimeoutError,
        hasVersionError: state => state.user.hasVersionError,
        allDomains: state => state.user.allDomains,
        isOffline: state => state.isOffline
      }
    ),
    ...mapGetters(
      {
        activeAuthSession: 'user/activeAuthSession'
      }
    ),
    filteredAuthSessions: function () {
      let authSessions = this.authSessions

      // Filter on searchString
      if (this.searchString.trim() !== '') {
        const searchLower = this.searchString.trim().toLowerCase()
        authSessions = this.authSessions.filter(session => {
          return session.user?.username.toLowerCase().includes(searchLower) ||
               session.domain?.name.toLowerCase().includes(searchLower) ||
               session.user?.namespace.toLowerCase().includes(searchLower)
        })
      }

      // Sort on 1) domain.name 2) user.username
      authSessions = authSessions.slice().sort((a, b) => {
        const domainCompare = a.domain?.name.localeCompare(b.domain?.name, 'sv')
        if (domainCompare !== 0) {
          return domainCompare
        }
        return a.user?.username.localeCompare(b.user?.username, 'sv')
      })

      return authSessions
    },
    user: function () {
      return this.activeAuthSession?.user
    },
    domain: function () {
      return this.activeAuthSession?.domain
    },
    token: function () {
      return this.activeAuthSession?.token
    },
    allDomainsOrdered: function () {
      // Sortera alfabetiskt
      return this.allDomains.slice().sort((a, b) => {
        return a.name.localeCompare(b.name, 'sv')
      })
    },
    toolbarTitle: function () {
      if (this.isOffline) {
        return 'Datorn är offline'
      } else if (this.hasTimeoutError) {
        return 'Uppkoppling avbruten'
      } else if (this.hasVersionError) {
        return 'Programmet behöver uppdateras'
      } else {
        return 'Inloggning'
      }
    },
    showError: function () {
      return (this.isOffline || this.hasTimeoutError || this.hasVersionError)
    },
    appVersion: function () {
      if (process.env.NODE_ENV === 'production') {
        return process.env.VUE_APP_VERSION
      } else {
        return process.env.VUE_APP_VERSION + '-dev'
      }
    },
    appVersionName: function () {
      return process.env.VUE_APP_VERSION_NAME
    }
  },
  methods: {
    logOutSession: function (authSessionId) {
      this.$store.dispatch('user/logoutAuthSession', { authSessionId })
        .then((response) => {
          if (response?.data?.status === 'success') {
            this.$store.dispatch('snackbars/createSnackbar', {
              color: 'success',
              text: 'Användaren har loggats ut'
            })
          } else {
            this.$store.dispatch('snackbars/createSnackbar', {
              color: 'error',
              text: 'Kunde inte logga ut användaren'
            })
          }
        })
    },
    logOutAndDeleteSession: function (authSessionId) {
      this.$store.dispatch('user/logoutAndDeleteAuthSession', { authSessionId })
        .then((response) => {
          if (response?.data?.status === 'success') {
            this.$store.dispatch('snackbars/createSnackbar', {
              color: 'success',
              text: 'Användaren har loggats ut'
            })
          } else {
            this.$store.dispatch('snackbars/createSnackbar', {
              color: 'error',
              text: 'Kunde inte logga ut användaren'
            })
          }
        })
    },
    loginWithAnotherAccount: function () {
      this.showLoginForm = true
    },
    backToSessionList: function () {
      this.showLoginForm = false
    },
    reconnectSession: function (authSessionId) {
      const session = this.authSessions.find(s => s.id === authSessionId)
      if (!session) {
        return false
      }

      if (!session.token) {
        // logga in igen, domän och username förifyllt
        this.domainId = session.domain.id
        this.username = session.user.username
        this.showLoginForm = true
        return false
      }

      this.isLoading = true
      this.validationError = ''
      this.$store.dispatch('user/reconnectSession', { authSessionId })
        .then((response) => {
          if (response?.data?.status === 'success') {
            // återanslutning lyckades
          } else {
            this.validationError = 'Kunde inte återansluta till servern.'
          }
        })
        .catch(() => {
          this.validationError = 'Kunde inte återansluta till servern.'
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    login: function () {
      this.validationError = ''
      const domain = this.allDomains.find(d => d.id === this.domainId)
      if (!domain) {
        this.validationError = 'Välj en domän'
      }
      if (this.password === '') {
        this.validationError = 'Ange lösenord'
      }
      if (this.username === '') {
        this.validationError = 'Ange användarnamn'
      }
      if (this.validationError) {
        return false
      }

      const username = this.username
      const password = this.password
      this.isLoading = true
      this.$store.dispatch('user/login', { domain, username, password })
        .then((data) => {
          if (data?.status !== 'success') {
            if (data?.error_code === 'invalid_credentials') {
              this.validationError = 'Felaktigt användarnamn eller lösenord.'
            } else {
              this.validationError = 'Ett oväntat fel inträffade'
            }
          }
        })
        .catch((err) => {
          console.error('Login error', err)
          this.validationError = 'Ett oväntat fel inträffade'
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    scrollSelectedCardIntoView: function () {
      // Make sure the selected card is visible
      this.$nextTick(() => {
        let selectedElement = null
        if (this.selectedCardIndex === this.filteredAuthSessions.length) {
          // "Lägg till ny användare" card
          selectedElement = this.$el.getElementsByClassName('login-card-add-new-account')[0]
        } else {
          selectedElement = this.$el.getElementsByClassName('session-card-index-' + this.selectedCardIndex)[0]
        }
        if (selectedElement) {
          selectedElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
      })
    },
    onKeyDown: function (event) {
      // Ignore key events if not showing session list
      if (this.showLoginForm) {
        return
      }

      // increment/decrement selectedCardIndex with arrow keys
      if (event.key === 'ArrowDown') {
        event.preventDefault() // För att inte scrolla sidan om elementet med scrollbaren är i fokus
        if (this.selectedCardIndex < this.filteredAuthSessions.length) {
          this.selectedCardIndex += 1
        } else {
          this.selectedCardIndex = 0
        }
        this.scrollSelectedCardIntoView()
        return
      } else if (event.key === 'ArrowUp') {
        event.preventDefault() // För att inte scrolla sidan om elementet med scrollbaren är i fokus
        if (this.selectedCardIndex > 0) {
          this.selectedCardIndex -= 1
        } else {
          this.selectedCardIndex = this.filteredAuthSessions.length
        }
        this.scrollSelectedCardIntoView()
        return
      } else if (event.key === 'Enter' || event.key === 'NumpadEnter' || event.key === ' ') {
        const selectedSession = this.filteredAuthSessions[this.selectedCardIndex]
        if (selectedSession) {
          this.reconnectSession(selectedSession.id)
        } else if (this.selectedCardIndex === this.filteredAuthSessions.length) {
          this.loginWithAnotherAccount()
        }
        return
      }

      // Ignore key events if search field is focused
      const activeElement = document.activeElement
      if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
        return
      }

      // Sökfältet visas endast om fler än 3 sessions, så ignorerar input också annars
      if (this.authSessions.length <= 3) {
        this.searchString = ''
        return
      }

      const key = event.key
      if (key.length === 1 && key.match(/^[a-zA-Z0-9åäöÅÄÖ._\-\s]$/)) {
        this.searchString += key
      } else if (key === 'Backspace') {
        // if ctrl + backspace, remove last word
        if (event.ctrlKey) {
          const parts = this.searchString.split(' ')
          parts.pop()
          this.searchString = parts.join(' ')
        } else {
          this.searchString = this.searchString.slice(0, -1)
        }
      } else if (key === 'Escape') {
        this.searchString = ''
      }
    }
  },
  watch: {
    username: function () {
      this.validationError = ''
    },
    password: function () {
      this.validationError = ''
    },
    domainId: function () {
      this.validationError = ''
    },
    filteredAuthSessions: function () {
      // Reset selectedCardIndex if out of bounds
      if (this.selectedCardIndex > this.filteredAuthSessions.length) {
        this.selectedCardIndex = 0
      }
    }
  },
  mounted () {
    // Capture keyboard letters to searchString
    window.addEventListener('keydown', this.onKeyDown)
  },
  destroyed () {
    // Remove event listener
    window.removeEventListener('keydown', this.onKeyDown)
  }
}
</script>
